import { type ChangeEvent, ReactElement, useCallback, useEffect, useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import {
  AutocompleteArrayInput,
  NullableBooleanInput,
  ReferenceArrayInput,
  required,
  SelectInput,
  type FilterPayload,
  type RaRecord,
} from 'react-admin';
import TzDateInput from '../../Components/TzDate/TzDateInput';
import { createFilterQuery } from '../../utils';
import { getPeriodDateRange, PERIOD_CHOICES } from './utils';

const setValueOptions = {
  shouldDirty: true,
  shouldTouch: true,
  shouldValidate: true,
};

function periodForDateRange(fromDate: unknown, toDate: unknown): string | undefined {
  if (typeof fromDate !== 'string' || typeof toDate !== 'string') {
    return undefined;
  }

  return PERIOD_CHOICES.find(({ id }) => {
    const range = getPeriodDateRange(id);
    return range?.from === fromDate && range.to === toDate;
  })?.id;
}

function periodFromChange(event: ChangeEvent<HTMLInputElement> | RaRecord | string): string {
  if (typeof event === 'string') {
    return event;
  }

  return 'target' in event ? event.target.value : String(event.id);
}

export function RegionFilter(): ReactElement {
  return (
    <ReferenceArrayInput source="region_ids" reference="regions" label="Region">
      <AutocompleteArrayInput
        filterToQuery={createFilterQuery(['name'])}
        optionText="attributes.name"
      />
    </ReferenceArrayInput>
  );
}

export function MerchantFilter(): ReactElement {
  const regionIds = useWatch({ name: 'region_ids' }) as (string | number)[] | undefined;

  const filter = useMemo<FilterPayload>(
    () => ({
      region: {
        id: regionIds?.length ? regionIds : undefined,
      },
    }),
    [regionIds],
  );

  return (
    <ReferenceArrayInput
      source="merchant_ids"
      reference="merchants"
      label="Merchant"
      filter={filter}
    >
      <AutocompleteArrayInput
        filterToQuery={createFilterQuery(['merchant_name'])}
        optionText="attributes.merchant_name"
      />
    </ReferenceArrayInput>
  );
}

export function HasOffersFilter(): ReactElement {
  return <NullableBooleanInput source="has_offers" label="Has Offers?" />;
}

export function PeriodFilter(): ReactElement {
  const { setValue } = useFormContext();
  const period = useWatch({ name: 'period' });
  const fromDate = useWatch({ name: 'greaterOrEqual.date' });
  const toDate = useWatch({ name: 'lessOrEqual.date' });

  const onPeriodChange = useCallback(
    (event: ChangeEvent<HTMLInputElement> | RaRecord | string) => {
      const range = getPeriodDateRange(periodFromChange(event));
      if (!range) {
        return;
      }

      setValue('greaterOrEqual.date', range.from, setValueOptions);
      setValue('lessOrEqual.date', range.to, setValueOptions);
    },
    [setValue],
  );

  useEffect(() => {
    const matchingPeriod = periodForDateRange(fromDate, toDate) ?? '';
    if (period !== matchingPeriod) {
      setValue('period', matchingPeriod, setValueOptions);
    }
  }, [fromDate, period, setValue, toDate]);

  return (
    <SelectInput
      source="period"
      label="Period"
      choices={PERIOD_CHOICES}
      onChange={onPeriodChange}
    />
  );
}

export function GranularityFilter(): ReactElement {
  return (
    <SelectInput
      source="granularity"
      label="Granularity"
      choices={[
        { id: 'daily', name: 'Daily' },
        { id: 'hourly', name: 'Hourly' },
      ]}
    />
  );
}

export function DateFromFilter(): ReactElement {
  return (
    <TzDateInput
      label="From"
      source="greaterOrEqual.date"
      ignoreTimezoneInfo
      validate={[required()]}
    />
  );
}

export function DateToFilter(): ReactElement {
  return (
    <TzDateInput label="To" source="lessOrEqual.date" ignoreTimezoneInfo validate={[required()]} />
  );
}
